<template>
  <section>
    <GlobalSearchForm
      :page="results.currentPage"
      :term="results.term"
      :searching="results.searched"
      :totals="results.totals"
      :esSize="results.sizeValue"
      v-on:update:page="updatePage($event)"
      v-on:update:searching="search($event)"
    />
    <div class="search-results-container">
      <Header
        v-if="!this.sticky && this.scrollUp && this.scrollNav"
        :class="{ sticky: this.scrollUp }"
        :primaryNav="headerPrimNav"
        :secondaryNav="headerSecNav"
        :museumInfo="headerMusInfo"
        :hasSearch="false"
      />
      <GlobalSearchResultsTotals
        :class="{ sticky: this.sticky }"
        :featureImages="featureImages"
        :featureImagesToggle="featureImagesToggle"
        :results="results"
        :toggledImage="showImages"
        v-on:update:image="updateImageToggle($event)"
        v-on:update:amount="updatePage($event)"
      />
      <SearchQuerySuggest
        v-if="results.suggestionTrigger"
        :suggest="results.suggestions"
        v-on:update:suggested="updateTerm($event)"
      />
      <GlobalSearchResults
        :class="{ 'sticky-padding': this.sticky }"
        :featureImages="featureImages"
        :featureTags="featureTags"
        :results="results.results"
        :showImages="showImages"
      />
      <PaginationNav
        class="search-pagination bottom f-ui-1"
        :detailed="true"
        :sizeValue="results.sizeValue"
        :fromValue="results.fromValue"
        :currentPage="results.currentPage"
        :totalHits="results.totals"
        :maxVisibleButtons="maxVisibleButtons"
        @updateSearch="updatePage($event)"
      />
    </div>
  </section>
</template>

<script>
import GlobalSearchForm from "@/components/GlobalSearchForm";
import GlobalSearchResults from "@/components/GlobalSearchResults";
import GlobalSearchResultsTotals from "@/components/GlobalSearchResultsTotals";
import HeaderData from "@thegetty/getty-ui/src/components/globals/header/data.json";
import naughtyStrings from "@/assets/blns.json";
import SearchQuerySuggest from "@/components/SearchQuerySuggest";

export default {
  name: "GlobalSearch",
  data() {
    return {
      headerPrimNav: HeaderData.primaryItems,
      headerSecNav: HeaderData.secondaryItems,
      headerMusInfo: HeaderData.museumInfo,
      percentScrolled: 0,
      scrollTop: 0,
      showImages: this.featureImages,
      trackLength: 0,
      rAF: null,
      results: {
        info: "global",
        term: "",
        currentPage: 1,
        endPage: 1,
        sizeValue: 20,
        fromValue: 0,
        totalPages: 1,
        results: [],
        suggestions: {},
        suggestionTrigger: false,
        totals: 0,
        searched: false
      },
      scrollNav: false,
      scrollBuffer: 50,
      scrollUp: false,
      scrollPosition: 0,
      scrollLastScrollDownPosition: 0,
      sticky: false
    };
  },
  props: {
    isMobile: Boolean
  },
  inject: ["runtimeConfig"],
  computed: {
    maxVisibleButtons() {
      if (this.isMobile) {
        return 5;
      } else {
        return 10;
      }
    },
    features() {
      return this.runtimeConfig.features;
    },
    featureImages() {
      return this.features && !!this.features.images;
    },
    featureImagesToggle() {
      return this.features && !!this.features.imagesToggle;
    },
    featureTags() {
      return this.features && !!this.features.tags;
    }
  },
  components: {
    GlobalSearchForm,
    GlobalSearchResults,
    GlobalSearchResultsTotals,
    SearchQuerySuggest
  },
  watch: {
    $route(to, from) {
      const tempObject = {};
      tempObject.term = to.query.qt;
      this.updateTerm(tempObject);
    }
  },
  created() {
    window.addEventListener("scroll", this.handleScroll);
    this.rAF = window.requestAnimationFrame(this.handleScroll);
  },
  destroyed() {
    window.removeEventListener("scroll", this.handleScroll);
    window.cancelAnimationFrame(this.rAF);
    this.rAF = null;
  },
  methods: {
    /*
     * Scroll checker and determine if it gets passed the header
     * or before the footer
     */
    handleScroll() {
      const y = window.scrollY;
      if (this.oldY !== y) {
        const header = document.querySelector(".g-header");
        const headerHeight = header ? header.clientHeight : 0;
        const form = document.querySelector(".global.form");
        const formHeight = form ? form.clientHeight : 0;
        const height = headerHeight + formHeight;
        const footer = document.querySelector(".g-footer");
        const footerHeight = footer ? footer.clientHeight : 0;
        const docHeight = this.getDocHeight();

        if (y > this.scrollPosition) {
          this.scrollLastScrollDownPosition = window.scrollY;
          this.scrollUp = false;
          if (y > height) {
            this.sticky = true;
          }
          if (y > docHeight - footerHeight * 1.25) {
            this.sticky = false;
          }
        } else if (y <= this.scrollLastScrollDownPosition - this.scrollBuffer) {
          this.scrollNav = true;
          this.scrollUp = true;
          this.sticky = false;
          if (y < headerHeight || y > docHeight - footerHeight * 1.25) {
            this.scrollNav = false;
          }
        }
      }
      this.oldY = y;
      this.scrollPosition = window.scrollY;
      this.rAF = window.requestAnimationFrame(this.handleScroll);
    },
    getDocHeight() {
      let D = document;
      return Math.max(
        D.body.scrollHeight,
        D.documentElement.scrollHeight,
        D.body.offsetHeight,
        D.documentElement.offsetHeight,
        D.body.clientHeight,
        D.documentElement.clientHeight
      );
    },
    updateTerm(value) {
      this.results.suggestionTrigger = false;
      this.results.term = value.term;
    },
    search(value) {
      this.results.endPage = value.endPage;
      this.results.fromValue = value.fromValue;
      this.results.results = value.results;
      this.results.searched = value.searched;
      this.results.sizeValue = value.sizeValue;
      this.results.suggestions = value.suggested;
      this.results.startResults = value.startResults;
      this.results.term = value.term;
      this.results.totalPages = value.totalPages;
      this.results.totals = value.totals;
      this.setResultsPath();
      this.suggestionCheck(value.suggested);
    },
    setResultsPath() {
      this.$router
        .push({
          name: "global",
          query: {
            qt: this.results.term,
            pg: this.results.currentPage
          }
        })
        .catch(error => {
          // do nothing
        });
    },
    suggestionCheck(value) {
      this.results.suggestionTrigger =
        value && value.suggestor[0] && value.suggestor[0].options.length > 0
          ? true
          : false;
    },
    nextClicked() {
      this.$emit("update:next");
      this.results.currentPage++;
    },
    updatePage(value) {
      this.$emit("update:page");
      this.results.currentPage = value.page;
    },
    updateImageToggle() {
      this.showImages = !this.showImages;
    },
    prevClicked() {
      this.$emit("update:prev");
      this.results.currentPage--;
    }
  }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
* {
  font-family: "Graphik Web";
  font-style: inherit;
  font-weight: inherit;
}
.date {
  text-align: right;
  font-size: $size-7;
}
.search-results-container {
  @include width(6.25, medium);
  @include breakpoint("small-") {
    @include width(6);
  }
  text-align: left;
  margin: 0 auto;
  .sticky {
    @include width(6.55, medium);
    background: $white;
    position: fixed;
    margin-top: 0;
    top: 0;
    padding-right: 3em;
    z-index: 400;
    @include breakpoint("small-") {
      width: 100%;
    }
  }
  &-card {
    padding: 0 1em;
  }
  .suggestion {
    margin: -1.5em 0 0;
    position: absolute;
  }
  .g-header {
    &.sticky {
      padding-right: 0;
      width: 100%;
    }
  }
}
.sticky-padding {
  margin-top: 7em;
}
.search-pagination {
  margin-top: $spacing-08;
  justify-content: center;
  &.bottom {
    margin-top: $spacing-10;
  }
  &.suggested {
    margin-top: -3.65em;
  }
  .pagination {
    align-items: baseline;
    .button.is-link,
    .pagination-numbers .pagination-item button {
      background-color: $white;
      color: $color__blue--60;
      &.active {
        color: $color__grey--60;
        font-weight: 400;
      }
    }
    .button.is-link[disabled] {
      span {
        color: $color__grey--60;
      }
    }
    .pagination-item {
      padding: 0 $spacing-02;
    }
  }
  &:after {
    clear: both;
  }
}
</style>
